import React from "react";
import styled from "styled-components";

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: ${({ theme }) => theme.colors.main.authBackground};
`;

const ErrorCard = styled.div`
  background: ${({ theme }) => theme.colors.main.white};
  border-radius: ${({ theme }) => theme.borderRadii.card};
  padding: 48px 32px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border.secondaryLight};
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.main.textMain};
  margin: 0 0 12px 0;
  line-height: 1.2;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: ${({ theme }) => theme.colors.main.primary};
  color: ${({ theme }) => theme.colors.main.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadii.button};
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.hover.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(32, 190, 173, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: transparent;
  color: ${({ theme }) => theme.colors.main.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border.secondary};
  border-radius: ${({ theme }) => theme.borderRadii.button};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.main.whiteBg};
    border-color: ${({ theme }) => theme.colors.border.primary};
    color: ${({ theme }) => theme.colors.main.textMain};
  }
`;

const NetworkHint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.main.textTertiary};
  margin: 16px 0 0 0;
  font-style: italic;
`;

const ErrorDisplay = ({ 
  error, 
  errorMessage, 
  onRetry, 
  onContinue, 
  retryText = "🔄 Try Again",
  continueText = "Continue to Dashboard"
}) => {
  const isNetworkError = error === "network_error";
  
  return (
    <ErrorContainer>
      <ErrorCard>
        <ErrorIcon>
          {isNetworkError ? "🌐" : "⚠️"}
        </ErrorIcon>
        
        <ErrorTitle>
          {isNetworkError ? "Connection Lost" : "Something went wrong"}
        </ErrorTitle>
        
        <ErrorMessage>
          {errorMessage || "We encountered an issue. Don't worry, your data is safe."}
        </ErrorMessage>
        
        <ButtonContainer>
          <PrimaryButton onClick={onRetry}>
            {retryText}
          </PrimaryButton>
          
          <SecondaryButton onClick={onContinue}>
            {continueText}
          </SecondaryButton>
        </ButtonContainer>
        
        {isNetworkError && (
          <NetworkHint>
            Check your internet connection and try again
          </NetworkHint>
        )}
      </ErrorCard>
    </ErrorContainer>
  );
};

export default ErrorDisplay;
